import { useState, useEffect } from "react";
import { apiFetch } from "../config";

const ETAPES = ["55SAGI1", "55GBS1", "55QIF1", "55BD1"];

const formatHeures = (secondes) => {
  const minutes = Math.round(secondes / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

function HeuresSemaine({ semaine, etape }) {
  const [etudiants, setEtudiants] = useState(null);
  const [erreur, setErreur] = useState("");
  const [tri, setTri] = useState({ cle: "nom", sens: 1 });

  useEffect(() => {
    setEtudiants(null);
    setErreur("");
    apiFetch(`/suivi/heures?semaine=${semaine}&etape=${encodeURIComponent(etape)}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(setEtudiants)
      .catch(() => setErreur("Erreur de chargement."));
  }, [semaine, etape]);

  if (erreur) return <p style={{ color: "crimson" }}>{erreur}</p>;
  if (!etudiants) return <p>Chargement…</p>;
  if (etudiants.length === 0) return <p>Aucun étudiant ni cours pour cette sélection.</p>;

  const trier = (cle) =>
    setTri((t) => (t.cle === cle ? { cle, sens: -t.sens } : { cle, sens: 1 }));

  const liste = [...etudiants].sort((a, b) => {
    const va = a[tri.cle];
    const vb = b[tri.cle];
    const c = typeof va === "string" ? va.localeCompare(vb, "fr") : va - vb;
    return (c || a.nom.localeCompare(b.nom, "fr") || a.prenom.localeCompare(b.prenom, "fr")) * (c ? tri.sens : 1);
  });

  const entete = (cle, label) => (
    <th style={{ ...styles.th, cursor: "pointer", userSelect: "none" }} onClick={() => trier(cle)}>
      {label} {tri.cle === cle ? (tri.sens === 1 ? "▲" : "▼") : "↕"}
    </th>
  );

  return (
    <table style={styles.table}>
      <thead>
        <tr>
          {entete("nom", "Nom")}
          {entete("prenom", "Prénom")}
          {entete("secondes_suivies", "Heures suivies")}
          {entete("secondes_total", "Heures totales")}
        </tr>
      </thead>
      <tbody>
        {liste.map((e) => (
          <tr key={e.num_etudiant}>
            <td style={styles.td}>{e.nom}</td>
            <td style={styles.td}>{e.prenom}</td>
            <td style={styles.td}>{formatHeures(e.secondes_suivies)}</td>
            <td style={styles.td}>{formatHeures(e.secondes_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Administration() {
  const [groupes, setGroupes] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [etape, setEtape] = useState(ETAPES[0]);
  const [groupe, setGroupe] = useState("");
  const [etapeListe, setEtapeListe] = useState(ETAPES[0]);
  const [etudiants, setEtudiants] = useState([]);
  const [erreur, setErreur] = useState("");

  const chargerAssociations = () =>
    apiFetch("/suivi/associations")
      .then((r) => r.json())
      .then(setAssociations)
      .catch(() => setErreur("Erreur de chargement."));

  useEffect(() => {
    chargerAssociations();
  }, []);

  useEffect(() => {
    setGroupe("");
    apiFetch(`/suivi/groupes?etape=${encodeURIComponent(etape)}`)
      .then((r) => r.json())
      .then(setGroupes)
      .catch(() => setErreur("Erreur de chargement."));
  }, [etape]);

  useEffect(() => {
    apiFetch(`/suivi/etudiants?etape=${encodeURIComponent(etapeListe)}`)
      .then((r) => r.json())
      .then(setEtudiants)
      .catch(() => setEtudiants([]));
  }, [etapeListe, associations]);

  const envoyer = async (method, a) => {
    setErreur("");
    const r = await apiFetch("/suivi/associations", {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(a),
    });
    if (!r.ok) setErreur("Opération impossible.");
    await chargerAssociations();
  };

  const ajouter = async () => {
    if (!groupe) return;
    await envoyer("POST", { etape, groupe });
    setGroupe("");
  };

  return (
    <div style={styles.admin}>
      <div style={styles.adminGauche}>
      {erreur && <p style={{ color: "crimson" }}>{erreur}</p>}
      <h3>Associations étape / groupe</h3>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Étape</th>
            <th style={styles.th}>Groupe</th>
            <th style={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {associations.map((a) => (
            <tr key={a.etape + "|" + a.groupe}>
              <td style={styles.td}>{a.etape}</td>
              <td style={styles.td}>{a.groupe}</td>
              <td style={styles.td}>
                <button onClick={() => envoyer("DELETE", a)}>Supprimer</button>
              </td>
            </tr>
          ))}
          <tr>
            <td style={styles.td}>
              <select value={etape} onChange={(e) => setEtape(e.target.value)}>
                {ETAPES.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </td>
            <td style={styles.td}>
              <select value={groupe} onChange={(e) => setGroupe(e.target.value)}>
                <option value="">Groupe…</option>
                {groupes.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </td>
            <td style={styles.td}>
              <button onClick={ajouter} disabled={!groupe}>Ajouter</button>
            </td>
          </tr>
        </tbody>
      </table>

      </div>

      <div style={styles.adminDroite}>
        <h3 style={{ marginTop: 0 }}>Étudiants par étape</h3>
        <div style={styles.onglets}>
          {ETAPES.map((e) => (
            <button
              key={e}
              onClick={() => setEtapeListe(e)}
              style={{ ...styles.onglet, ...(e === etapeListe ? styles.onglet_actif : {}) }}
            >
              {e}
            </button>
          ))}
        </div>
        <p style={{ margin: "8px 0" }}>
          <b>{etudiants.length}</b> étudiant(s)
        </p>
        {etudiants.length === 0 ? (
          <p>Aucun étudiant : associe au moins un groupe à cette étape.</p>
        ) : (
          <div style={styles.liste}>
            <table style={styles.table}>
              <tbody>
                {etudiants.map((e) => (
                  <tr key={e.num_etudiant}>
                    <td style={styles.td}>{e.nom}</td>
                    <td style={styles.td}>{e.prenom}</td>
                    <td style={styles.td}>{e.num_etudiant}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const ONGLETS = [
  { id: "heures", label: "Nbre heures / semaine", composant: HeuresSemaine, filtres: true },
  { id: "admin", label: "Administration", composant: Administration, filtres: false },
];

export default function Suivi() {
  const [semaines, setSemaines] = useState([]);
  const [semaine, setSemaine] = useState("");
  const [etape, setEtape] = useState("");
  const [onglet, setOnglet] = useState(ONGLETS[0].id);

  useEffect(() => {
    apiFetch("/suivi/semaines")
      .then((r) => r.json())
      .then(setSemaines)
      .catch(() => setSemaines([]));
  }, []);

  const actif = ONGLETS.find((o) => o.id === onglet);
  const Composant = actif.composant;

  return (
    <div style={styles.page}>
      <h2>Suivi des présences</h2>
      <div style={styles.onglets}>
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            onClick={() => setOnglet(o.id)}
            style={{ ...styles.onglet, ...(o.id === onglet ? styles.onglet_actif : {}) }}
          >
            {o.label}
          </button>
        ))}
      </div>

      {actif.filtres && (
        <div style={styles.filtres}>
          <select value={semaine} onChange={(e) => setSemaine(e.target.value)}>
            <option value="">Semaine…</option>
            {semaines.map((s) => (
              <option key={s} value={s}>Semaine {s}</option>
            ))}
          </select>
          <select value={etape} onChange={(e) => setEtape(e.target.value)}>
            <option value="">Étape…</option>
            {ETAPES.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
      )}

      {!actif.filtres || (semaine && etape) ? (
        <Composant semaine={semaine} etape={etape} />
      ) : (
        <p>Choisis une semaine et une étape.</p>
      )}
    </div>
  );
}

const styles = {
  admin: { display: "flex", gap: 24, alignItems: "flex-start" },
  adminGauche: { flex: 1, minWidth: 0 },
  adminDroite: { flex: 1, minWidth: 0, position: "sticky", top: 16 },
  liste: { maxHeight: "70vh", overflowY: "auto", border: "1px solid #eee" },
  page: { maxWidth: 1300, margin: "0 auto", padding: 24, fontFamily: "sans-serif" },
  filtres: { display: "flex", gap: 12, marginBottom: 16 },
  onglets: { display: "flex", gap: 4, borderBottom: "1px solid #ccc", marginBottom: 16 },
  onglet: { padding: "8px 16px", border: "none", background: "none", cursor: "pointer" },
  onglet_actif: { borderBottom: "3px solid #1976d2", fontWeight: "bold" },
  table: { borderCollapse: "collapse", width: "100%" },
  th: { textAlign: "left", borderBottom: "2px solid #ccc", padding: 8 },
  td: { borderBottom: "1px solid #eee", padding: 8 },
};
